import { useParams } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { createAppeal, fetchAppealById, updateAppeal } from 'api/appeals';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Appeal } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface AppealFormValues {
    name: string;
    code: string | null;
    active: boolean;
}

const AppealsFormDrawer = () => {
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { goBack } = useDialogRoute();
    const { appealId: id } = useParams<{ appealId?: string }>();
    const appealId = id ? parseInt(id) : undefined;

    const { data: appeal } = useSuspenseQuery({
        queryKey: accountQueryKeys.appealById(accountId, appealId as number),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchAppealById(accountId, appealId as number),
                !!appealId,
            ),
    });

    const defaultValues = {
        name: appeal?.name ?? '',
        code: appeal?.code ?? '',
        active: appeal?.status === 'active',
    };

    const methods = Form.useGivebutterForm<AppealFormValues>({ defaultValues });
    const { control, path, handleSubmit, setError, resetFormButton } = methods;

    const { mutate: submit, isPending: isSubmitting } = useMutation<
        Appeal,
        GivebutterAxiosError,
        AppealFormValues
    >({
        mutationFn: values => {
            const data = {
                ...values,
                status: values.active ? 'active' : 'inactive',
            };
            return appeal
                ? updateAppeal(accountId, appeal.id, data)
                : createAppeal(accountId, data);
        },
        onSuccess: () => {
            success(`Appeal ${appeal ? 'updated' : 'created'} successfully!`);
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.appeals(accountId) });
            goBack();
        },
        onError: e => {
            error(e.response?.data.message || 'Something went wrong. Please try again.');
            const errors = e.response?.data.errors;

            if (errors) {
                for (const field in errors) {
                    setError(field as keyof AppealFormValues, {
                        type: 'server',
                        message: errors[field][0],
                    });
                }
            }
            resetFormButton();
        },
    });

    return (
        <Form.GivebutterFormProvider {...methods}>
            <form className="contents" onSubmit={handleSubmit(values => submit(values))}>
                <Drawer.Header>
                    <Drawer.Title>{appeal ? 'Update' : 'Add'} appeal</Drawer.Title>
                </Drawer.Header>
                <Drawer.Body className="flex flex-col gap-6">
                    <Form.Container>
                        <Form.Label name={path('name')}>
                            Appeal name
                            <Form.LabelAsterisk />
                        </Form.Label>
                        <Form.TextInputRoot
                            control={control}
                            name="name"
                            rules={{ required: 'Please enter a name' }}
                        >
                            <Form.TextInputForm />
                        </Form.TextInputRoot>
                        <Form.Error name={path('name')} />
                    </Form.Container>
                    <Form.Container>
                        <Form.Label name={path('code')}>Appeal code</Form.Label>
                        <Form.TextInputRoot control={control} name="code">
                            <Form.TextInputForm />
                        </Form.TextInputRoot>
                        <Form.Error name={path('code')} />
                    </Form.Container>
                    <Form.Container className="flex flex-row gap-2">
                        <Form.UseFormCheckboxInput control={control} name={path('active')} />
                        <div className="flex flex-col gap-1">
                            <Form.Label
                                name={path('active')}
                                className="cursor-pointer font-medium leading-none"
                            >
                                Active appeal
                            </Form.Label>
                            <p className="text-sm text-gray-600">
                                This is a current appeal your organization is using.
                            </p>
                        </div>
                    </Form.Container>
                </Drawer.Body>
                <Drawer.Footer>
                    <div className="flex w-full flex-wrap-reverse items-center justify-between gap-2">
                        <Drawer.Cancel asChild={true}>
                            <Button variant="outline" color="neutral">
                                Cancel
                            </Button>
                        </Drawer.Cancel>
                        <Form.SubmitButton isSubmitting={isSubmitting}>
                            {appeal ? 'Update appeal' : 'Add appeal'}
                        </Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.GivebutterFormProvider>
    );
};

export { AppealsFormDrawer };
